import React, { useEffect, useState } from 'react';
import Button, {
  ButtonSize,
  ButtonType
} from '../../../../../shared/components/core-ui/button/button.component';
import Modal from '../../../../../shared/components/core-ui/modal/modal.component';
import { UserRole } from '../../../../../shared/enums/user-role.enum';
import { ReservationStatus } from '../../../../../shared/enums/reservation-status';
import { ReservationDetailsUiModel } from '../../../../../shared/ui-models/reservation.ui-model';
import { updateOneReservations } from '../../../../shared/services/data.service';
import { birdToast } from '../../../../../shared/components/core-ui/toast';

export interface ActualCheckInOutProps {
  data: {
    reservation: ReservationDetailsUiModel;
  };
  config: {
    userRole: any;
    userRoles: any;
  };
  eventHandlers: {
    onGetOneReservation: (id: string) => void;
  };
}

const CAN_EDIT_ROLES = [
  UserRole.HOSPITALITY_AGENT,
  UserRole.HOSPITALITY_MANAGER,
  UserRole.GES,
  UserRole.ADMIN,
  UserRole.SUPER_ADMIN
];

export const ActualCheckInOut: React.FC<ActualCheckInOutProps> = ({
  data: { reservation },
  config: { userRole, userRoles },
  eventHandlers: { onGetOneReservation }
}) => {
  const [actualCheckInDate, setActualCheckInDate] = useState<Date | null>(null);
  const [actualCheckOutDate, setActualCheckOutDate] = useState<Date | null>(null);
  const [pendingActualCheckOutDate, setPendingActualCheckOutDate] = useState<Date | null>(null);
  const [showCheckOutConfirmModal, setShowCheckOutConfirmModal] = useState<boolean>(false);
  const [isSavingActualCheckIn, setIsSavingActualCheckIn] = useState<boolean>(false);
  const [isSavingActualCheckOut, setIsSavingActualCheckOut] = useState<boolean>(false);

  useEffect(() => {
    if (!reservation) return;
    setActualCheckInDate(
      reservation.actualCheckInAt ? new Date(reservation.actualCheckInAt) : null
    );
    setActualCheckOutDate(
      reservation.actualCheckOutAt ? new Date(reservation.actualCheckOutAt) : null
    );
  }, [reservation]);

  const canEditActualCheckInOut =
    CAN_EDIT_ROLES.indexOf(userRole) > -1 ||
    CAN_EDIT_ROLES.some((item) => userRoles?.includes(item));

  const canEditActualDates =
    reservation &&
    canEditActualCheckInOut &&
    ![
      ReservationStatus.CANCELED,
      ReservationStatus.EXPIRED,
      ReservationStatus.CONFIRMATION_PENDING
    ].includes(reservation.status as ReservationStatus);

  const handleSaveActualCheckIn = () => {
    if (!reservation) return;
    const now = new Date();
    setIsSavingActualCheckIn(true);
    const promise = updateOneReservations({
      id: reservation.id,
      actualCheckInAt: now.getTime()
    }).then(() => {
      onGetOneReservation(reservation.id);
      setActualCheckInDate(now);
      setIsSavingActualCheckIn(false);
    });
    birdToast.promise(promise, {
      loadingMessage: 'Saving actual check-in time...',
      successMessage: 'Actual check-in time saved successfully.',
      errorFailedMessage: 'Failed to save actual check-in time.'
    });
  };

  const handleSaveActualCheckOut = () => {
    if (!reservation) return;
    setPendingActualCheckOutDate(new Date());
    setShowCheckOutConfirmModal(true);
  };

  const confirmSaveActualCheckOut = () => {
    if (!reservation) return;
    const checkOutDate = pendingActualCheckOutDate || new Date();
    setIsSavingActualCheckOut(true);
    const promise = updateOneReservations({
      id: reservation.id,
      actualCheckOutAt: checkOutDate.getTime()
    }).then(() => {
      onGetOneReservation(reservation.id);
      setActualCheckOutDate(checkOutDate);
      setIsSavingActualCheckOut(false);
      setPendingActualCheckOutDate(null);
      setShowCheckOutConfirmModal(false);
    });
    birdToast.promise(promise, {
      loadingMessage: 'Saving actual check-out time...',
      successMessage: 'Actual check-out time saved successfully.',
      errorFailedMessage: 'Failed to save actual check-out time.'
    });
  };

  return (
    <>
      <Modal
        config={{ showModal: showCheckOutConfirmModal, fullScreen: false, showClose: true }}
        eventHandlers={{
          closeModalHandler: () => {
            setShowCheckOutConfirmModal(false);
            setPendingActualCheckOutDate(null);
          }
        }}
      >
        <div className="p-8">
          <div className="font-semibold text-[1.8rem] text-[#566346] mb-4">
            Confirm Actual Check-out
          </div>
          <div className="font-[Abel] text-[1.4rem] text-[#333] mb-4 leading-normal">
            Are you sure you want to record the actual check-out time? This action may affect
            billing and reporting.
          </div>
          <div className="font-[Abel] text-[1.4rem] text-[#237c58] font-bold mb-6 p-3 bg-[#f5f5f5] rounded">
            Check-out time: {pendingActualCheckOutDate?.toLocaleString()}
          </div>
          <div className="flex gap-4 justify-end">
            <Button
              onClick={() => {
                setShowCheckOutConfirmModal(false);
                setPendingActualCheckOutDate(null);
              }}
              type={ButtonType.primaryInverted}
              size={ButtonSize.medium}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmSaveActualCheckOut}
              size={ButtonSize.medium}
              loading={isSavingActualCheckOut}
            >
              Confirm
            </Button>
          </div>
        </div>
      </Modal>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Actual Check-in */}
          <div className="flex flex-col gap-2 p-3 rounded-lg border border-gray-100 bg-gray-50/50">
            <span className="text-base font-gilroy-bold font-bold uppercase tracking-wide text-neutral-500">
              Actual Check-in
            </span>
            {actualCheckInDate ? (
              <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                <div className="text-xl font-semibold text-green-800">
                  {actualCheckInDate.toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </div>
                <div className="text-lg text-green-600 mt-1">
                  {actualCheckInDate.toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
            ) : (
              canEditActualDates && (
                <Button
                  onClick={handleSaveActualCheckIn}
                  size={ButtonSize.small}
                  disabled={isSavingActualCheckIn}
                  loading={isSavingActualCheckIn}
                >
                  Check-in Now
                </Button>
              )
            )}
          </div>

          {/* Actual Check-out */}
          <div className="flex flex-col gap-2 p-3 rounded-lg border border-gray-100 bg-gray-50/50">
            <span className="text-base font-gilroy-bold font-bold uppercase tracking-wide text-neutral-500">
              Actual Check-out
            </span>
            {actualCheckOutDate ? (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                <div className="text-xl font-semibold text-blue-800">
                  {actualCheckOutDate.toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </div>
                <div className="text-lg text-blue-600 mt-1">
                  {actualCheckOutDate.toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
            ) : (
              canEditActualDates && (
                <Button
                  onClick={handleSaveActualCheckOut}
                  size={ButtonSize.small}
                  disabled={isSavingActualCheckOut}
                  loading={isSavingActualCheckOut}
                >
                  Check-out Now
                </Button>
              )
            )}
          </div>
        </div>
      </div>
    </>
  );
};
